import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { getEnv } from '@hub/helpers/getters.store';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { PagePath } from '@monorepo/client-types';
import {
  useBooleanFeatureFlag,
  WithWaitForFeature,
} from '@monorepo/feature-flag/client';
import { FLAG } from '@monorepo/feature-flag/shared';
import { IsLoggedOutKey } from '@monorepo/types';
import { QuickPurchaseErrorModal } from './components/quick-purchase-error-modal';
import { QuickPurchaseModal } from './components/quick-purchase-modal';
import { QuickPurchaseSkeleton } from './components/quick-purchase-skeleton';
import { REDIRECT_DELAY_MS } from './constants/quick-purchase.constants';
import {
  QuickPurchaseFlowStep,
  useQuickPurchaseFlow,
} from './hooks/use-quick-purchase-flow';

const QuickPurchaseGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const isEnabled = useBooleanFeatureFlag(FLAG.ENABLE_QUICK_PURCHASE, {
    env: EnvService.application,
  });

  useEffect(() => {
    if (!isEnabled) {
      navigate('/', { replace: true });
    }
  }, [isEnabled, navigate]);

  if (!isEnabled) return null;

  return <>{children}</>;
};

const QuickPurchasePage = observer(() => {
  const navigate = useNavigate();
  const {
    dataStore: { userStore },
  } = useStore();

  const { step, error, handleError, reinitFlow } = useQuickPurchaseFlow();

  const isError = step === QuickPurchaseFlowStep.Error;

  // Keep a ref to userStore so the timeout callback reads the latest values.
  // We can't put MobX observables in useEffect deps (causes infinite loops),
  // but the ref lets us read current values when the timeout fires.
  const userStoreRef = useRef(userStore);
  userStoreRef.current = userStore;

  useEffect(() => {
    if (!isError) return;

    const timer = setTimeout(async () => {
      // Redirect to asset selection without source=quick_purchase — the user
      // is leaving the quick-purchase flow, so they should have normal Hub
      // navigation (back button visible, etc.).
      const assetSelectionUrl = '/purchase?step=assets';

      // Read latest values from the ref to get current MobX state
      const store = userStoreRef.current;
      const isLoggedIn = store.isLoggedIn;
      const isAnonymous = Boolean(store.currentUser?.isAnonymous);

      if (isLoggedIn && !isAnonymous) {
        // Real authenticated user — redirect directly to asset selection.
        // Going to /login would just bounce them back here via initialUrl anyway.
        navigate(assetSelectionUrl, { replace: true });
      } else {
        // Anonymous session (e.g. country-excluded after OTP) — clear it fully
        // before sending to login. Without this, Descope auto-skips login (live
        // JWT), and the private router's catch-all redirects straight to
        // initialUrl because isLoggedIn is still true in MobX.
        localStorage.setItem('initialUrl', assetSelectionUrl);
        void getEnv().apiFactory.quickPurchaseService.logRedirect('error_flow');
        await getEnv().apiFactory.authenticationService.logout();
        localStorage.setItem(IsLoggedOutKey, 'true');
        store.setLoginState(false);
        navigate(PagePath.Login, { replace: true });
      }
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isError, navigate]);

  const showModal =
    step === QuickPurchaseFlowStep.ModalEmail ||
    step === QuickPurchaseFlowStep.ModalOtp ||
    step === QuickPurchaseFlowStep.ModalCountry ||
    step === QuickPurchaseFlowStep.CreatingAnon;

  return (
    <>
      <QuickPurchaseSkeleton />
      {isError && <QuickPurchaseErrorModal error={error} />}
      {showModal && (
        <QuickPurchaseModal
          onError={handleError}
          onAuthenticated={reinitFlow}
        />
      )}
    </>
  );
});

const QuickPurchasePageWithGuard = () => (
  <WithWaitForFeature timeout={5000} fallback={null}>
    <QuickPurchaseGuard>
      <QuickPurchasePage />
    </QuickPurchaseGuard>
  </WithWaitForFeature>
);

export default QuickPurchasePageWithGuard;
